import { Box, Tooltip, Typography } from '@mui/material';
import cx from 'classnames';
import { format } from 'date-fns';
import React, { FC, createRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getActiveTicket, getIsStepperStagesOpen } from 'store/ticket-service/selector';
import { setIsStepperStagesOpen } from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';

import { NamespacesList, Statuses } from 'core/enums/common';
import useTooltip from 'core/hooks/useTooltip';

import { TicketStepper } from 'components/TicketSteps/TicketStepper';
import { TitleText } from 'components/shared/CustomText/TitleText';

import { ReactComponent as ArrowRight } from 'assets/icons/small-arrow-right.svg';

import styles from '../Sidebar.module.scss';

interface SidebarTicketProps {
	ticket: TicketType;
	handleTicketClick: (ticket: TicketType) => void;
}

export const SidebarTicket: FC<SidebarTicketProps> = ({ ticket, handleTicketClick }) => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();

	const isStepperStagesOpen = useSelector(getIsStepperStagesOpen);
	const activeTicket = useSelector(getActiveTicket);

	const itemTitleRef = createRef<HTMLDivElement>();
	const isTooltipVisible = useTooltip(itemTitleRef);

	const handleTicketExpand = () => {
		dispatch(setIsStepperStagesOpen(!isStepperStagesOpen));
	};

	const isActiveTicket = useMemo(() => ticket.id === activeTicket?.id, [ticket, activeTicket]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<Box
				sx={{
					boxSizing: 'border-box',
					display: 'flex',
					gap: '8px',
					cursor: 'pointer',
					width: '100%',
					height: '50px',
					background: isActiveTicket ? '#ECF3FF' : '#fff',
					borderRadius: '8px',
					padding: '8px 12px',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
				onClick={() => handleTicketClick(ticket)}
			>
				<Box onClick={handleTicketExpand} sx={{ padding: '4px', flexShrink: 0 }}>
					<ArrowRight style={{ transform: `rotate(${isStepperStagesOpen ? '90deg' : '0deg'})` }} />
				</Box>

				<Box
					sx={{
						flexDirection: 'column',
						justifyContent: 'space-between',
						flexGrow: 1,
						minWidth: 0,
					}}
				>
					<Tooltip
						componentsProps={{
							tooltip: {
								sx: {
									backgroundColor: '#f5f5f9',
									color: 'rgba(0, 0, 0, 0.87)',
									fontSize: '12px',
									maxWidth: 250,
									border: '1px solid #dadde9',
									'& .MuiTooltip-popper': { margin: 0 },
								},
							},
						}}
						PopperProps={{
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, -15],
									},
								},
							],
						}}
						title={
							isTooltipVisible
								? ticket.title.includes('defaultTicketTitle') ||
								  ticket.title.includes('New request')
									? commonTranslate('text.defaultTicketTitle')
									: ticket.title
								: null
						}
						placement="top"
					>
						<TitleText
							text={
								ticket.title.includes('defaultTicketTitle') || ticket.title.includes('New request')
									? commonTranslate('text.defaultTicketTitle')
									: ticket.title
							}
							styles={{
								fontSize: '14px',
								whiteSpace: 'nowrap',
								textOverflow: 'ellipsis',
								overflow: 'hidden',
							}}
						/>
					</Tooltip>

					<Typography sx={{ color: '#707A8E', fontSize: '10px' }}>
						{format(new Date(ticket.createdDate), 'dd/MM/yyyy')}
					</Typography>
				</Box>
				{ticket && (
					<Box>
						<Typography
							className={cx(
								styles.status,
								styles[`status_${Statuses[ticket.status].split(' ').join('').toLowerCase()}`]
							)}
							sx={{
								fontSize: '10px',
								padding: '4px 8px',
							}}
						>
							{commonTranslate(`ticketStatuses.${Statuses[ticket.status]}`)}
						</Typography>
					</Box>
				)}
			</Box>
			{isStepperStagesOpen && isActiveTicket ? <TicketStepper /> : null}
		</Box>
	);
};
